import { Recognition, CoreValue } from "@shared/models";
import { all, call, put, takeLatest, takeEvery, takeLeading } from "redux-saga/effects";
import { PaginatedResponse, ActionWithPayload, WithCallback } from "@shared/interfaces";
import { startLoading, stopLoading } from "@shared/store/actions";
import { RecognitionFilter } from "@shared/interfaces/RecognitionFilter.interface";
import { BULK_APPROVE_SKIP_REASON } from "@shared/constants";
import {
  BulkApproveDto,
  BulkApproveResult,
  CreateRecognitionDto,
  CreateRecognitionSuccess,
  UpdateRecognitionDto,
} from "@containers/Recognition/interfaces";

import {
  getRecognition,
  getCoreValues,
  createRecognition,
  getReceivedRecognitions,
  getSentRecognitions,
  getReviewRecognitions,
  updateRecognition,
  bulkApproveRecognitions,
  getPendingCount,
  getRecognitionCounts,
} from "./actions";
import api from "../api";

// Human-readable label per skip reason, used only to compose the toast message below -- the wire
// value itself (e.g. "not_in_review") is never shown to a reviewer.
const SKIP_REASON_LABELS: Record<BULK_APPROVE_SKIP_REASON, string> = {
  [BULK_APPROVE_SKIP_REASON.FLAGGED]: "flagged",
  [BULK_APPROVE_SKIP_REASON.NOT_IN_REVIEW]: "no longer in review",
  [BULK_APPROVE_SKIP_REASON.NOT_FOUND]: "not found",
};

// The api's `message` is a static constant with no counts (api/app/shared/constants/
// successMessages.ts:38, SUCCESS_MESSAGE.RECOGNITIONS_BULK_APPROVED, used at
// api/app/controllers/recognition.controller.ts:383). errorHandlerMiddleware (src/store/store.tsx:38-45)
// auto-toasts whatever `payload.message` a *_SUCCESS action carries, so replacing it here -- with
// real counts AND a per-reason breakdown, not just "Approved N" -- is what turns that auto-toast into
// something useful. Exported so saga.test.tsx can assert its output directly rather than re-deriving
// the same string.
export const composeBulkApproveMessage = (response: BulkApproveResult): string => {
  const approvedCount = response.approved.length;
  const skippedCount = response.skipped.length;

  if (!skippedCount) {
    return `Approved ${approvedCount}.`;
  }

  // Partial, not Record<BULK_APPROVE_SKIP_REASON, number> -- a reason absent from `skipped` has no key
  // here at all, and typing it as a plain (non-partial) Record would let a read of that absent key
  // through as `number` when it is actually `undefined`. Built non-mutating (a fresh object per
  // iteration via spread) rather than mutating one shared accumulator.
  const countByReason = response.skipped.reduce<Partial<Record<BULK_APPROVE_SKIP_REASON, number>>>(
    (acc, { reason }) => ({ ...acc, [reason]: (acc[reason] ?? 0) + 1 }),
    {},
  );

  // Object.values(BULK_APPROVE_SKIP_REASON), not Object.keys(countByReason) -- gives the breakdown a
  // fixed, deterministic order regardless of the order the api happened to return skipped rows in.
  const breakdown = Object.values(BULK_APPROVE_SKIP_REASON)
    .filter((reason) => countByReason[reason])
    .map((reason) => `${countByReason[reason] ?? 0} ${SKIP_REASON_LABELS[reason]}`)
    .join(", ");

  return `Approved ${approvedCount}. Skipped ${skippedCount} (${breakdown})`;
};

// Exported (unlike the other workers below) so store-level tests can drive it directly and assert
// the `is_clear: !payload.page` derivation the RecognitionListContainer's `page: 0` reset relies on
// — mirrors the sparks/ sibling saga's own export-every-worker convention (store/sparks/saga.tsx).
export function* getReceivedRecognitionsSaga({ payload }: ActionWithPayload<RecognitionFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<Recognition> = yield call(api.getRecognitions, payload);
    yield put(
      getReceivedRecognitions.success({
        ...response,
        is_clear: !payload.page,
      }),
    );
  } catch (error) {
    yield put(getReceivedRecognitions.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

// Exported for the same reason as getReceivedRecognitionsSaga above.
export function* getSentRecognitionsSaga({ payload }: ActionWithPayload<RecognitionFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<Recognition> = yield call(api.getRecognitions, payload);
    yield put(
      getSentRecognitions.success({
        ...response,
        is_clear: !payload.page,
      }),
    );
  } catch (error) {
    yield put(getSentRecognitions.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

// Exported for the same reason as getReceivedRecognitionsSaga above.
export function* getReviewRecognitionsSaga({ payload }: ActionWithPayload<RecognitionFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<Recognition> = yield call(api.getRecognitions, payload);
    yield put(
      getReviewRecognitions.success({
        ...response,
        is_clear: !payload.page,
      }),
    );
  } catch (error) {
    yield put(getReviewRecognitions.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

// Exported (like the three workers above) so saga.test.tsx's whole-list registration test can
// reference this exact function: that assertion enumerates EVERY registration in recognitionSagas by
// identity, so every worker has to be an importable reference.
export function* getRecognitionSaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    const response: Recognition = yield call(api.getRecognition, payload);
    yield put(
      getRecognition.success({
        ...response,
      }),
    );
  } catch (error) {
    yield put(getRecognition.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

// Exported for the same reason as getRecognitionSaga above.
export function* getCoreValuesSaga() {
  try {
    yield put(startLoading());
    const response: CoreValue[] = yield call(api.getCoreValues);
    yield put(getCoreValues.success(response));
  } catch (error) {
    yield put(getCoreValues.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

// Exported for the same reason as getRecognitionSaga above.
export function* createRecognitionSaga({ payload }: ActionWithPayload<WithCallback<CreateRecognitionDto>>) {
  try {
    const { cb, ...rest } = payload;
    yield put(startLoading());

    const response: CreateRecognitionSuccess = yield call(api.createRecognition, rest);

    yield put(createRecognition.success(response));
    if (cb) {
      cb();
    }
  } catch (error) {
    yield put(createRecognition.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

// Exported for the same reason as getRecognitionSaga above.
//
// 923-core-context context/engineering/spark-rewards/phase-53-decline-reason-and-decision-buttons.md PR3: `cb` destructured out before the api call, the
// same shape createRecognitionSaga above uses -- `rest` (never the raw payload) is what reaches
// api.updateRecognition, so `cb` itself is never sent as a field on the PUT body. Fires only on success,
// never on failure, matching every other cb-bearing saga in this file.
export function* updateRecognitionSaga({ payload }: ActionWithPayload<WithCallback<UpdateRecognitionDto>>) {
  try {
    const { cb, ...rest } = payload;
    yield put(startLoading());

    const response: CreateRecognitionSuccess = yield call(api.updateRecognition, rest);

    yield put(updateRecognition.success(response));
    if (cb) {
      cb();
    }
  } catch (error) {
    yield put(updateRecognition.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

// Exported for the same reason as getRecognitionSaga above -- saga.test.tsx's whole-list registration
// test compares this worker against its takeLeading registration BY REFERENCE, which is what pins the
// choice of takeLeading over takeLatest here (see the registration itself below for why that matters).
export function* bulkApproveRecognitionsSaga({ payload }: ActionWithPayload<WithCallback<BulkApproveDto>>) {
  try {
    const { cb, ...rest } = payload;
    yield put(startLoading());

    const response: BulkApproveResult = yield call(api.bulkApprove, rest);

    yield put(bulkApproveRecognitions.success({ ...response, message: composeBulkApproveMessage(response) }));
    if (cb) {
      cb();
    }
  } catch (error) {
    yield put(bulkApproveRecognitions.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

// D5: deliberately NO startLoading/stopLoading -- this is a background poll, and wrapping it would
// flash the app's global loader every 30s with nobody having asked for anything.
export function* getPendingCountSaga() {
  try {
    const response: { count: number } = yield call(api.getPendingCount);
    yield put(getPendingCount.success(response));
  } catch {
    // D6: bare .failure(), no payload -- see actions.tsx's own comment on getPendingCount for why.
    yield put(getPendingCount.failure());
  }
}

// D4: the refresh worker for the array-pattern watcher below. Its ENTIRE body is a single put -- it
// re-dispatches getPendingCount.request() rather than forking getPendingCountSaga itself, so every
// real fetch funnels through the ONE takeLatest(getPendingCount.request, ...) channel below. That is
// what closes the race where a poll response and a mutation-triggered response could otherwise land
// out of order. Do NOT add a delay/call/suspending select here -- takeLatest holds one shared task
// slot per REGISTRATION (not per pattern member), so the array pattern below cross-cancels; this stays
// safe only because this worker is never suspended in a cancellable state. Full reasoning in the
// phase-42 implementation plan, §3.
export function* refreshPendingCountSaga() {
  yield put(getPendingCount.request());
}

// page-header-promotion-plan.md: no endpoint returns a summed received+sent total, so this reads TWO
// pages of the same api.getRecognitions the received/sent lists already use -- `limit: 1` since only
// `count` (the paginated total) is read, the one row each response carries back is discarded. Run in
// parallel via `all`, not two sequential `call`s, so a slow received fetch cannot delay sent.
// Deliberately no startLoading/stopLoading, like getPendingCountSaga above: this fetch runs on every
// Rewards section mount (the header persists across every tab), and wrapping it would flash the app's
// global loader on a navigation nobody asked to load anything for.
export function* getRecognitionCountsSaga({ payload }: ActionWithPayload<number>) {
  try {
    const [received, sent]: [PaginatedResponse<Recognition>, PaginatedResponse<Recognition>] = yield all([
      call(api.getRecognitions, { page: 0, limit: 1, search: "", user_id: payload }),
      call(api.getRecognitions, { page: 0, limit: 1, search: "", user_created_by_id: payload }),
    ]);
    yield put(getRecognitionCounts.success({ received: received.count, sent: sent.count }));
  } catch (error) {
    yield put(getRecognitionCounts.failure(error as Error));
  }
}

function* recognitionSagas() {
  yield takeLatest(getReceivedRecognitions.request, getReceivedRecognitionsSaga);
  yield takeLatest(getSentRecognitions.request, getSentRecognitionsSaga);
  yield takeLatest(getReviewRecognitions.request, getReviewRecognitionsSaga);
  yield takeLatest(getRecognition.request, getRecognitionSaga);
  yield takeLatest(getCoreValues.request, getCoreValuesSaga);
  yield takeLatest(createRecognition.request, createRecognitionSaga);
  // takeEvery, not takeLatest: approve/decline are independent per-row mutations. Under takeLatest,
  // clicking two rows in quick succession cancels the first saga task — its PUT still commits
  // server-side, but updateRecognition.success is never dispatched for it, so that row silently
  // stays in the review queue even though the api already approved/declined it.
  yield takeEvery(updateRecognition.request, updateRecognitionSaga);
  // takeLeading, not takeLatest: the container has no click guard of its own on "Approve selected"
  // (its button's `disabled={... || isBulkApproving}` only takes effect after a real redux round trip,
  // too late to stop two synchronous clicks), so this registration is the ONLY thing enforcing "only
  // one bulk approve in flight". takeLeading runs the FIRST request and drops every subsequent one
  // until it completes. takeLatest would instead CANCEL the in-flight request and start a new one,
  // which for a request that already committed approvals server-side silently loses whichever ids the
  // cancelled task never got to dispatch bulkApproveRecognitions.success for. Same choice, and the
  // same reasoning, as authSagas' BOOTSTRAP registration (Auth/store/saga.tsx:109-113).
  yield takeLeading(bulkApproveRecognitions.request, bulkApproveRecognitionsSaga);
  yield takeLatest(getPendingCount.request, getPendingCountSaga);
  // D4: ONE registration reacting to BOTH mutation successes, re-dispatching rather than forking the
  // fetch worker directly -- see refreshPendingCountSaga's own comment above for why this is safe only
  // because that worker never suspends.
  yield takeLatest([updateRecognition.success, bulkApproveRecognitions.success], refreshPendingCountSaga);
  yield takeLatest(getRecognitionCounts.request, getRecognitionCountsSaga);
}

export default recognitionSagas;
